import { useEffect, useState, useCallback } from "react";
import { Loader2, RefreshCw, CalendarClock, FileWarning, Gavel, CalendarDays, Save, Plus, X, AlertOctagon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DataTable } from "@/components/common/DataTable";
import { api, apiError } from "@/lib/api";
import { fdate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// PLD Bloque 2 · Pieza C — tablero de plazos (28ª trimestral / 29ª-30ª inusual / 33ª LPB 24h / 43ª-II).
const SEM = {
  rojo: { cls: "bg-red-100 text-red-700", label: "Rojo" },
  ambar: { cls: "bg-amber-100 text-amber-700", label: "Ámbar" },
  verde: { cls: "bg-emerald-100 text-emerald-700", label: "Verde" },
};

function Semaforo({ value }) {
  const s = SEM[value] || SEM.verde;
  return <Badge className={`gap-1 ${s.cls}`} data-testid={`sem-${value}`}><span className="h-2 w-2 rounded-full bg-current" /> {s.label}</Badge>;
}

function Kpi({ label, value, tone = "text-primary", testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <p className={`mt-1 font-heading text-2xl font-semibold ${tone}`}>{value}</p>
    </div>
  );
}

export default function TableroPlazos() {
  const { user } = useAuth();
  const canSync = ["oficial_cumplimiento", "admin_entidad"].includes(user?.role);
  const canEditInhabiles = user?.role === "oficial_cumplimiento";

  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inhOpen, setInhOpen] = useState(false);
  const [inh, setInh] = useState({ anio: new Date().getFullYear(), dias: [] });
  const [nuevoDia, setNuevoDia] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/pld/plazos/tablero");
      setData(data);
    } catch (e) { console.debug("Tablero de plazos no disponible (área restringida):", e?.response?.status); }
  }, []);

  // Barrido bajo demanda automático al montar (estilo cobranza/sincronizar), solo si puede.
  useEffect(() => {
    (async () => {
      if (canSync) {
        try { await api.post("/pld/plazos/sincronizar"); } catch (e) { console.debug("sincronizar plazos:", e?.response?.status); }
        try { await api.post("/pld/acciones/sincronizar"); } catch (e) { console.debug("sincronizar acciones:", e?.response?.status); }
      }
      await load();
    })();
  }, [canSync, load]);

  const sincronizar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pld/plazos/sincronizar");
      try { await api.post("/pld/acciones/sincronizar"); } catch { /* noop */ }
      toast.success(`Barrido de plazos: ${data.casos_evaluados} caso(s), ${data.reportes_sin_acuse} reporte(s) sin acuse, ${data.avisos_registrados} aviso(s) al calendario.`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const abrirInhabiles = async () => {
    try {
      const { data } = await api.get(`/pld/plazos/dias-inhabiles?anio=${new Date().getFullYear()}`);
      setInh({ anio: data.anio, dias: data.dias });
      setInhOpen(true);
    } catch (e) { toast.error(apiError(e)); }
  };

  const guardarInhabiles = async () => {
    setBusy(true);
    try {
      await api.put("/pld/plazos/dias-inhabiles", inh);
      toast.success("Calendario de días inhábiles guardado.");
      setInhOpen(false);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!data) return null;
  const k = data.kpis || {};
  const corte = data.corte_trimestral;

  return (
    <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="tablero-plazos">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h4 className="flex items-center gap-2 font-heading text-sm font-semibold text-slate-700">
          <CalendarClock className="h-4 w-4 text-primary" /> Tablero de cumplimiento de plazos PLD
        </h4>
        <div className="flex items-center gap-2">
          {canEditInhabiles && (
            <Button data-testid="dias-inhabiles-btn" size="sm" variant="outline" onClick={abrirInhabiles}>
              <CalendarDays className="mr-1.5 h-4 w-4" /> Días inhábiles
            </Button>
          )}
          {canSync && (
            <Button data-testid="sincronizar-plazos-btn" size="sm" disabled={busy} onClick={sincronizar} className="bg-accent hover:bg-forest-700">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} Actualizar plazos
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testid="kpi-rojos" label="En rojo (urgente)" value={k.rojos ?? 0} tone="text-red-600" />
        <Kpi testid="kpi-ambar" label="En ámbar (por vencer)" value={k.ambar ?? 0} tone="text-amber-600" />
        <Kpi testid="kpi-casos-vencidos" label="Dictámenes vencidos" value={k.casos_vencidos ?? 0} tone="text-red-600" />
        <Kpi testid="kpi-reportes-acuse" label="Reportes sin acuse" value={k.reportes_pendientes_acuse ?? 0} tone="text-amber-600" />
      </div>

      {/* Revisión de la metodología EBR (disp. 17a-4): por tiempo (12m) o por nueva ENR */}
      {data.revision_metodologia && (
        <div className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 ${
          data.revision_metodologia.semaforo === "rojo" ? "border-red-200 bg-red-50"
          : data.revision_metodologia.semaforo === "ambar" ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}
          data-testid="plazos-revision-ebr">
          <div className="flex items-center gap-2 text-sm">
            <CalendarClock className={`h-4 w-4 ${data.revision_metodologia.semaforo === "rojo" ? "text-red-600" : data.revision_metodologia.semaforo === "ambar" ? "text-amber-600" : "text-emerald-600"}`} />
            <span className="font-semibold text-slate-800">Metodología EBR v{data.revision_metodologia.version} — {data.revision_metodologia.etiqueta}</span>
            <span className="text-xs text-slate-500">
              {data.revision_metodologia.estado === "enr_pendiente"
                ? `Marcada por nueva ENR: ${data.revision_metodologia.enr_motivo || ""}`
                : `Límite ${fdate(data.revision_metodologia.fecha_limite)} · ${data.revision_metodologia.dias_restantes} día(s)`}
            </span>
            {data.revision_metodologia.tarea_remediacion && data.revision_metodologia.tarea_remediacion.estatus === "abierta" && (
              <span className="text-xs font-medium text-slate-700" data-testid="plazos-tarea-remediacion">
                · 📌 Remediación: {data.revision_metodologia.tarea_remediacion.responsable_nombre} · objetivo {fdate(data.revision_metodologia.tarea_remediacion.fecha_objetivo)}
              </span>
            )}
          </div>
          <Semaforo value={data.revision_metodologia.semaforo} />
        </div>
      )}

      {/* Corte trimestral de Relevantes (28ª) */}
      {corte && (
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="corte-trimestral">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm">
              <CalendarDays className="h-4 w-4 text-primary" />
              <span className="font-semibold text-slate-800">Reporte trimestral de Relevantes (28ª) — {corte.trimestre}</span>
            </div>
            <Semaforo value={corte.semaforo} />
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
            <div><p className="text-xs text-slate-500">Periodos cubiertos</p><p className="font-medium">{corte.periodos_cubiertos?.join(", ")}</p></div>
            <div><p className="text-xs text-slate-500">Ventana de envío</p><p className="font-medium">{fdate(corte.fecha_inicio_ventana)} – {fdate(corte.fecha_limite)}</p></div>
            <div><p className="text-xs text-slate-500">Op. relevantes</p><p className="font-medium">{corte.operaciones_relevantes} · {corte.reportes_generados} reporte(s)</p></div>
            <div><p className="text-xs text-slate-500">Estatus</p><p className="font-medium">{corte.estatus}</p></div>
          </div>
        </div>
      )}

      {/* Casos con dictamen pendiente (29ª/30ª/33ª) */}
      <div>
        <h5 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600"><Gavel className="h-3.5 w-3.5" /> Dictámenes con plazo</h5>
        <DataTable testid="table-plazos-casos" rows={data.casos}
          columns={[
            { key: "folio", label: "Folio" },
            { key: "cliente", label: "Cliente" },
            { key: "plazo_tipo", label: "Plazo" },
            { key: "fecha_limite_dictamen", label: "Límite", render: (r) => fdate(r.fecha_limite_dictamen) },
            { key: "dias_restantes", label: "Restante", render: (r) => r.dias_restantes == null ? "—" : `${r.dias_restantes} día(s)` },
            { key: "semaforo", label: "Semáforo", render: (r) => <Semaforo value={r.semaforo} /> },
          ]}
          emptyLabel="Sin dictámenes con plazo activo." />
      </div>

      {/* Reportes generados sin acuse (Pieza B) */}
      <div>
        <h5 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600"><FileWarning className="h-3.5 w-3.5" /> Reportes pendientes de acuse SITI</h5>
        <DataTable testid="table-plazos-reportes" rows={data.reportes_sin_acuse}
          columns={[
            { key: "archivo_nombre", label: "Archivo" },
            { key: "tipo", label: "Tipo" },
            { key: "periodo", label: "Periodo" },
            { key: "generado_en", label: "Generado", render: (r) => fdate(r.generado_en) },
            { key: "dias_desde_generacion", label: "Días", render: (r) => r.dias_desde_generacion == null ? "—" : `${r.dias_desde_generacion}` },
            { key: "semaforo", label: "Semáforo", render: (r) => <Semaforo value={r.semaforo} /> },
          ]}
          emptyLabel="Todos los reportes tienen acuse capturado." />
      </div>

      {/* Tareas de acción automática (DDR GAFI / desviación EBR) */}
      {(data.tareas_accion || []).length > 0 && (
        <div data-testid="tareas-accion">
          <h5 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600"><AlertOctagon className="h-3.5 w-3.5" /> Tareas de acción automática (DDR / desviación EBR)</h5>
          <DataTable testid="table-tareas-accion" rows={data.tareas_accion}
            columns={[
              { key: "titulo", label: "Tarea" },
              { key: "fecha", label: "Fecha", render: (r) => fdate(r.fecha) },
              { key: "semaforo", label: "Semáforo", render: (r) => <Semaforo value={r.semaforo} /> },
            ]} emptyLabel="Sin tareas de acción." />
        </div>
      )}

      {/* Diálogo: días inhábiles bancarios (solo OC) */}
      <Dialog open={inhOpen} onOpenChange={setInhOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="dias-inhabiles-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Días inhábiles bancarios {inh.anio}</DialogTitle>
            <DialogDescription className="text-xs text-amber-700">
              # REVISIÓN HUMANA — Validar/actualizar cada año contra el calendario oficial CNBV/Banxico. Determina el corte trimestral (28ª).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {inh.dias.map((d) => (
                <Badge key={d} variant="outline" className="gap-1" data-testid={`inh-${d}`}>
                  {d}
                  {canEditInhabiles && (
                    <button onClick={() => setInh((p) => ({ ...p, dias: p.dias.filter((x) => x !== d) }))} className="ml-1 text-red-500"><X className="h-3 w-3" /></button>
                  )}
                </Badge>
              ))}
              {inh.dias.length === 0 && <p className="text-xs text-slate-400">Sin días configurados.</p>}
            </div>
            <div className="flex items-center gap-2">
              <Input data-testid="inh-nuevo" type="date" value={nuevoDia} onChange={(e) => setNuevoDia(e.target.value)} className="h-9" />
              <Button data-testid="inh-add-btn" size="sm" variant="outline" onClick={() => { if (nuevoDia && !inh.dias.includes(nuevoDia)) { setInh((p) => ({ ...p, dias: [...p.dias, nuevoDia].sort() })); setNuevoDia(""); } }}>
                <Plus className="mr-1 h-4 w-4" /> Agregar
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInhOpen(false)}>Cancelar</Button>
            <Button data-testid="inh-guardar-btn" disabled={busy} onClick={guardarInhabiles} className="bg-primary hover:bg-sidebar-hover">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
